import logoVerticalSVG from "../../../public/images/logo_vertical.svg"
import step1SVG from "../../../public/images/virtueller-assistent/step1.svg"
import step2SVG from "../../../public/images/virtueller-assistent/step2.svg"
import step3SVG from "../../../public/images/virtueller-assistent/step3.svg"

import { create } from "zustand"
import { immer } from "zustand/middleware/immer"
import { z } from "zod"

const DEBUG = false

const initialData = DEBUG
  ? {
      booked_employee: null,
      booked_type: null,
      booked_date: null,
      booked_time: null,
      firstname: "Max",
      lastname: "Mustermann",
      email: "max.mustermann@voll.digital",
      phone: "+491234567890",
      concern: "planning_application",
    }
  : {
      booked_employee: null,
      booked_type: null,
      booked_date: null,
      booked_time: null,
      firstname: null,
      lastname: null,
      email: null,
      phone: null,
      concern: null,
    }

export const useFormStore = create(
  immer((set, get) => ({
    currentStep: 0,
    steps: [
      {
        id: 0,
        step_type: "title_step",
        title: "Architekt on demand",
        image: logoVerticalSVG,
      },
      {
        id: 1,
        step_type: "question_step",
        progress: 25,
        question: "Wählen Sie Ihren Wunschtermin",
        image: step1SVG,
        validation_schema: z.object({ booked_employee: z.string() }),
        fields: [
          {
            field_type: "booking_employee",
            name: "booked_employee",
          },
        ],
      },
      {
        id: 2,
        step_type: "question_step",
        progress: 25,
        question: "Wählen Sie Ihren Wunschtermin",
        image: step1SVG,
        validation_schema: z.object({ booked_type: z.string() }),
        fields: [
          {
            field_type: "booking_type",
            employee_key: "booked_employee",
            name: "booked_type",
          },
        ],
      },
      {
        id: 3,
        step_type: "question_step",
        progress: 25,
        question: "Wählen Sie Ihren Wunschtermin",
        image: step1SVG,
        validation_schema: z.object({ booked_date: z.string() }),
        fields: [
          {
            field_type: "booking_date",
            employee_key: "booked_employee",
            appointment_type_key: "booked_type",
            name: "booked_date",
          },
        ],
      },
      {
        id: 4,
        step_type: "question_step",
        progress: 25,
        question: "Wählen Sie Ihren Wunschtermin",
        image: step1SVG,
        validation_schema: z.object({ booked_time: z.string() }),
        fields: [
          {
            field_type: "booking_time",
            date_key: "booked_date",
            employee_key: "booked_employee",
            appointment_type_key: "booked_type",
            name: "booked_time",
          },
        ],
      },
      {
        id: 5,
        step_type: "question_step",
        progress: 25,
        question: "Geben Sie zunächst ihre Kontaktdaten an",
        image: step2SVG,
        validation_schema: z.object({
          firstname: z.string(),
          lastname: z.string(),
          email: z.string(),
          phone: z.string(),
          concern: z.string(),
          arc_additional_info: z.string().max(1500, "Description should not exceed 1500 characters"),
        }),
        fields: [
          {
            field_type: "content",
            contents: [{ content_type: "headline", text: "Persönliche Daten" }],
          },
          {
            field_type: "text",
            name: "firstname",
            label: "Vorname*",
            placeholder: "Vorname",
          },
          {
            field_type: "text",
            name: "lastname",
            label: "Nachname*",
            placeholder: "Nachname",
          },
          {
            field_type: "text",
            name: "email",
            label: "E-Mail-Adresse*",
            placeholder: "E-Mail-Adresse",
          },
          {
            field_type: "text",
            name: "phone",
            label: "Telefonnummer*",
            placeholder: "Telefonnummer",
          },
          {
            field_type: "content",
            contents: [{ content_type: "text", text: "*Pflichtfelder" }],
          },
          {
            field_type: "content",
            contents: [
              { content_type: "headline", text: "Termin-Details" },
              {
                content_type: "text",
                text: "Teilen Sie uns mit, worum es in ihrem Termin gehen soll.",
              },
            ],
          },
          {
            field_type: "select",
            name: "concern",
            options: [
              { value: "planning_application", label: "Bauantrag" },
              { value: "other", label: "Anderes Anliegen" },
            ],
            label: "Ihr Anliegen",
            placeholder: "Bitte auswählen",
          },
          {
            field_type: "textarea",
            name: "arc_additional_info",
            label: "Teilen Sie uns hier optional weitere Informationen zu Ihrem Projekt mit",
            placeholder: "",
          },
        ],
      },
      {
        id: 6,
        step_type: "overview_step",
        question: "Bitte überprüfen Sie Ihre Angaben",
        image: step3SVG,
        validation_schema: z.object({
          consented: z.string(),
        }),
        fields: [
          {
            field_type: "check",
            name: "consented",
            appearance: "small",
            label:
              "Ich akzeptiere die <a href='/datenschutz' target='_blank'>Datenschutzerklärung</a> und die <a href='/agb' target='_blank'>Allgemeinen Geschäftsbedingungen</a> und stimme der Verarbeitung meiner Daten gemäß der Datenschutzerklärung zu.",
          },
        ],
      },
    ],
    data: initialData,
    getStep: () => get().steps[get().currentStep],
    nextStep: () =>
      set((state) => {
        if (state.currentStep + 1 >= state.steps.length - 1) {
          state.currentStep = state.steps.length - 1
        } else {
          state.currentStep = state.currentStep + 1
        }
      }),
    prevStep: () =>
      set((state) => {
        if (state.currentStep <= 0) {
          state.currentStep = 0
        } else {
          state.currentStep = state.currentStep - 1
        }
      }),
    gotoStep: (index) =>
      set((state) => {
        if (index >= 0 && index <= state.steps.length - 1) {
          state.currentStep = index
        }
      }),
    setData: (key, value) =>
      set((state) => {
        state.data[key] = value
      }),
    resetStore: () =>
      set((state) => {
        state.data = initialData
        state.currentStep = 0
      }),
  }))
)
